import React, { useState } from 'react';
import { X, CreditCard, Wallet, TrendingUp, User, Building2, AlertTriangle, RefreshCw, Download, Eye } from 'lucide-react';
import { Transaction } from '../../types';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onRefund: (transactionId: string, reason: string) => void;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onRefund
}) => {
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !transaction) return null;

  const getTypeIcon = (type: string) => {
    const iconConfig = {
      wallet_load: <Wallet className="h-5 w-5 text-green-600" />,
      card_spend: <CreditCard className="h-5 w-5 text-blue-600" />,
      credit_advance: <TrendingUp className="h-5 w-5 text-purple-600" />,
    };
    return iconConfig[type as keyof typeof iconConfig] || <CreditCard className="h-5 w-5" />;
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      wallet_load: { color: 'bg-green-100 text-green-800', text: 'Wallet Load' },
      card_spend: { color: 'bg-blue-100 text-blue-800', text: 'Card Spend' },
      credit_advance: { color: 'bg-purple-100 text-purple-800', text: 'Credit Advance' },
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.card_spend;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', text: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', text: 'Failed' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const canRefund = transaction.status === 'completed' && transaction.type === 'card_spend';

  const handleRefund = async () => {
    if (!refundReason.trim()) return;
    
    setIsProcessing(true);
    try {
      await onRefund(transaction.id, refundReason);
      setShowRefundForm(false);
      setRefundReason('');
    } catch (error) {
      console.error('Error processing refund:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setShowRefundForm(false);
      setRefundReason('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              {getTypeIcon(transaction.type)}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
              <p className="text-sm text-gray-600">#{transaction.id}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Transaction Overview */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Transaction Type</span>
                {getTypeBadge(transaction.type)}
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                {getStatusBadge(transaction.status)}
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Amount</span>
                <span className="text-lg font-semibold text-gray-900">
                  ₦{transaction.amount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Date</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(transaction.date).toLocaleDateString()}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Time</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(transaction.date).toLocaleTimeString()}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Reference</span>
                <span className="text-sm font-medium text-gray-900 font-mono">
                  {transaction.id.slice(-8).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Information</h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <User className="h-4 w-4 text-gray-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">Employee</p>
                <p className="text-sm font-medium text-gray-900">{transaction.employeeName}</p>
                <p className="text-xs text-gray-500">{transaction.employeeId}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Building2 className="h-4 w-4 text-gray-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">Description</p>
                <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
              </div>
            </div>
            
            {transaction.type === 'card_spend' && (
              <div className="flex items-start space-x-3">
                <CreditCard className="h-4 w-4 text-gray-400 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Card Transaction</p>
                  <p className="text-sm font-medium text-gray-900">Corporate Card</p>
                  <p className="text-xs text-gray-500">Card ending in ****1234</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Actions</h3>
            <div className="flex items-center space-x-2">
              <button className="btn-secondary">
                <Download className="h-4 w-4 mr-2" />
                Export Receipt
              </button>
              <button className="btn-secondary">
                <Eye className="h-4 w-4 mr-2" />
                View Receipt
              </button>
            </div>
          </div>

          {/* Refund Section */}
          {canRefund && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Refund Transaction</h4>
                {!showRefundForm && (
                  <button
                    onClick={() => setShowRefundForm(true)}
                    className="btn-secondary"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Request Refund
                  </button>
                )}
              </div>
              
              {showRefundForm && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Refund *
                    </label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Explain why this refund is necessary..."
                      rows={3}
                      disabled={isProcessing}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowRefundForm(false)}
                      disabled={isProcessing}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRefund}
                      disabled={!refundReason.trim() || isProcessing}
                      className="btn-danger disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Process Refund
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Refund Not Available */}
          {!canRefund && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center space-x-2 text-gray-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">
                  {transaction.status !== 'completed' 
                    ? 'Refunds are only available for completed transactions'
                    : 'Refunds are only available for card spend transactions'
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
